import { Link } from 'react-router-dom';
import React, { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { RelatedLocations } from './RelatedLocations';
import { LocationFAQ } from './LocationFAQ';
import { SEO } from './SEO';
import { 
  Server, Zap, Shield, Globe, 
  CheckCircle, HardDrive, Network,
  Clock, MapPin, TrendingUp, Database,
  Building2, Radio, Activity, Eye
} from 'lucide-react';
import { Button } from './ui/button';
import { PricingSection } from './PricingSection';
import { PaymentMethodsSection } from './PaymentMethodsSection';
import { smoothScrollToWithOffset } from '../lib/scroll';

export const LatviaPage = () => {
  const { t, language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { icon: Clock, value: t('latvia.stats.latency'), label: t('latvia.stats.latencyLabel') },
    { icon: Zap, value: t('latvia.stats.speed'), label: t('latvia.stats.speedLabel'), wiki: 'shared-1gbps-port-explained' },
    { icon: TrendingUp, value: t('latvia.stats.uptime'), label: t('latvia.stats.uptimeLabel') },
    { icon: MapPin, value: t('latvia.stats.location'), label: t('latvia.stats.locationLabel') }
  ];

  const whyLatvia = [
    {
      icon: Globe,
      title: t('latvia.why.gateway.title'),
      description: t('latvia.why.gateway.desc')
    },
    {
      icon: Server,
      title: t('latvia.why.infrastructure.title'),
      description: t('latvia.why.infrastructure.desc')
    },
    {
      icon: Shield,
      title: t('latvia.why.compliance.title'),
      description: t('latvia.why.compliance.desc')
    },
    {
      icon: Network,
      title: t('latvia.why.connectivity.title'),
      description: t('latvia.why.connectivity.desc')
    }
  ];

  const technical = [
    {
      icon: Network,
      title: t('latvia.technical.network.title'),
      description: t('latvia.technical.network.desc')
    },
    {
      icon: Database,
      title: t('latvia.technical.storage.title'),
      description: t('latvia.technical.storage.desc')
    },
    {
      icon: Zap,
      title: t('latvia.technical.power.title'),
      description: t('latvia.technical.power.desc')
    },
    {
      icon: Eye,
      title: t('latvia.technical.monitoring.title'),
      description: t('latvia.technical.monitoring.desc')
    }
  ];

  const useCases = [
    {
      icon: MapPin,
      title: t('latvia.useCases.regional.title'),
      description: t('latvia.useCases.regional.desc')
    },
    {
      icon: TrendingUp,
      title: t('latvia.useCases.fintech.title'),
      description: t('latvia.useCases.fintech.desc')
    },
    {
      icon: Radio,
      title: t('latvia.useCases.media.title'),
      description: t('latvia.useCases.media.desc')
    },
    {
      icon: Building2,
      title: t('latvia.useCases.business.title'),
      description: t('latvia.useCases.business.desc')
    }
  ];

  const scrollToPricing = () => smoothScrollToWithOffset('pricing', 100);

  return (
    <div className="min-h-screen bg-white">
      <SEO page="latvia" />
      <Header />
      
      {/* Hero Section - Latvian Maroon/Dark Red theme */}
      <section className="relative py-20 bg-gradient-to-br from-red-900 via-red-800 to-red-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-red-700 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-red-800 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-800/20 border border-red-700/30 rounded-full text-red-50 text-sm font-semibold mb-6">
              <MapPin className="w-4 h-4" />
              <span>{t('latvia.hero.badge')}</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {t('latvia.hero.title')}
            </h1>
            <p className="text-xl text-red-50 max-w-3xl mx-auto mb-8">
              {t('latvia.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={scrollToPricing}
                className="bg-white text-red-800 hover:bg-red-50 font-semibold px-8 py-6 text-lg"
              >
                {t('latvia.hero.getStarted')}
              </Button>
              <Button 
                onClick={scrollToPricing}
                className="bg-red-950 text-white hover:bg-black font-semibold px-8 py-6 text-lg"
              >
                {t('latvia.hero.viewPlans')}
              </Button>
            </div>
          </div>
          
          {/* Riga Old Town Image */}
          <div className="rounded-2xl overflow-hidden max-w-4xl mx-auto shadow-2xl">
            <img src="/images/locations/unsplash_1685470934582-3636319a3be9.jpg" 
              alt="Riga Old Town Medieval Architecture" loading="lazy"
              className="w-full h-96 object-cover"
 loading="lazy"/>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-xl mb-4">
                  <stat.icon className="w-8 h-8 text-red-900" />
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
                {stat.wiki && (
                  <Link
                    to={(language === 'ru' ? '/ru' : '') + '/wiki/' + stat.wiki}
                    className="mt-2 inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 transition-colors"
                  >
                    {language === 'ru' ? 'Что значит общий 1 Гбит/с?' : 'What does shared 1 Gbps mean?'} →
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Latvia Section */}
      <section className="py-16 bg-gradient-to-b from-red-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('latvia.why.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('latvia.why.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {whyLatvia.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-8 border-2 border-red-200 hover:border-red-800 hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <item.icon className="w-7 h-7 text-red-900" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Infrastructure Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('latvia.technical.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('latvia.technical.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {technical.map((item, index) => (
              <div key={index} className="text-center p-6 bg-gradient-to-b from-red-50 to-white rounded-xl border border-red-200">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-xl mb-4">
                  <item.icon className="w-8 h-8 text-red-900" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Latvia Network Infrastructure Image */}
          <div className="mt-12 rounded-2xl overflow-hidden shadow-xl">
            <img src="/images/locations/unsplash_1544197150-b99a580bb7a8.jpg" 
              alt="Latvia Network Infrastructure" loading="lazy"
              className="w-full h-80 object-cover"
 loading="lazy"/>
          </div>
        </div>
      </section>      {/* Related Locations - Internal Links for SEO */}
      <RelatedLocations currentLocation="vps-latvia" />



      {/* Pricing Section */}
      <section id="pricing" className="pt-16 pb-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('latvia.pricing.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('latvia.pricing.subtitle')}
            </p>
          </div>
        </div>
        <PricingSection hideHeader={true} />
        
        {/* Payment Methods Section */}
        <div className="pt-16">
          <PaymentMethodsSection transparent={true} />
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('latvia.useCases.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('latvia.useCases.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-gradient-to-br from-red-50 to-white p-6 rounded-xl border-2 border-red-100 hover:border-red-800 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <useCase.icon className="w-6 h-6 text-red-900" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{useCase.title}</h3>
                <p className="text-gray-600 text-sm">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Details */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('latvia.location.title')}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                {t('latvia.location.subtitle')}
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed">
                {t('latvia.location.about')}
              </p>
              <ul className="space-y-3">
                {t('latvia.location.benefits').map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-900 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img src="/images/locations/unsplash_1758744799383-58c5d55aa003.jpg" 
                alt="Modern Riga Skyline and Business District" loading="lazy"
                className="w-full h-96 object-cover"
 loading="lazy"/>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-900 via-red-800 to-red-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-700 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-800 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('latvia.cta.title')}
          </h2>
          <p className="text-xl text-red-50 mb-8 max-w-2xl mx-auto">
            {t('latvia.cta.subtitle')}
          </p>
          <Button 
            onClick={scrollToPricing}
            className="bg-white text-red-900 hover:bg-red-50 font-bold px-10 py-6 text-xl"
          >
            {t('latvia.cta.button')}
          </Button>
        </div>
      </section>

      <LocationFAQ location="vps-latvia" />
      <Footer />
    </div>
  );
};


